import { Inject, Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Event,
  NavigationEnd,
  Router,
} from '@angular/router';
import { Store } from '@ngrx/store';
import { Angulartics2Segment } from 'angulartics2';
import { combineLatest, ReplaySubject } from 'rxjs';
import { filter, map, pairwise, take, tap } from 'rxjs/operators';

import { BaseAnalyticsConfig } from './analytics.module';

import { ANALYTICS_CONFIG_TOKEN } from './analytics.token';
import { TENANT_PARTNER_TYPES } from '@libs/src/tenant/tenant-partner-types.enum';
import { Tenant } from '@libs/src/tenant/tenant.interface';
import { IAppState } from '@main-client/src/app/app.state';

@Injectable()
export class AnalyticsService {
  readonly pageViewEventName = 'Page Viewed';
  eventsToTrack = new ReplaySubject();

  constructor(
    @Inject(ANALYTICS_CONFIG_TOKEN) public baseConfig: BaseAnalyticsConfig,
    public segment: Angulartics2Segment,
    public router: Router,
    public store: Store<IAppState>,
  ) {}

  initialize() {
    const tenantInfo = this.store.select('tenant').pipe(
      filter((tenant: Tenant) => !!tenant && 0 < Object.keys(tenant).length),
      map((tenant: Tenant) => ({
        tenantFriendlyName: tenant.friendly_name,
        tenantName: tenant.name,
        tenantPartnerType: tenant.partner_type,
      })),
    );

    this.trackRouterEvents();
    combineLatest([tenantInfo.pipe(take(1)), this.eventsToTrack]).subscribe(
      ([tenant, eventToTrack]: [
        {
          tenantFriendlyName: string;
          tenantName: string;
          tenantPartnerType: TENANT_PARTNER_TYPES;
        },
        { eventName: string; properties: { [key: string]: any } },
      ]) => {
        const snapshot = this.router.routerState.root.snapshot;
        this.segment.eventTrack(eventToTrack.eventName, {
          ...this.baseConfig,
          ...tenant,
          currentUrl: this.router.url,
          rawCurrentUrl: this.getRawUrlStructure(snapshot) || '/',
          ...eventToTrack.properties,
        });
      },
    );
  }

  eventTrack(eventName: string, properties: any): void {
    this.eventsToTrack.next({ eventName, properties });
  }

  private trackRouterEvents() {
    const routerEventsSource = this.router.events.pipe(
      filter((event: Event) => event instanceof NavigationEnd),
    );
    routerEventsSource
      .pipe(
        take(1),
        tap((currentEvent: any) =>
          this.trackPageView(currentEvent.urlAfterRedirects),
        ),
      )
      .subscribe();
    routerEventsSource
      .pipe(
        pairwise(),
        tap(([previousEvent, currentEvent]: any[]) => {
          this.trackPageView(
            currentEvent.urlAfterRedirects,
            previousEvent.urlAfterRedirects,
          );
        }),
      )
      .subscribe();
  }

  trackPageView(currentUrl: string, fromUrl: string = null): void {
    const snapshot = this.router.routerState.root.snapshot;
    this.eventTrack(this.pageViewEventName, {
      currentUrl,
      fromUrl,
      queryParams: this.getPropertiesFromRouterTree(snapshot, 'queryParams'),
      rawCurrentUrl: this.getRawUrlStructure(snapshot) || '/',
      routerParams: this.getPropertiesFromRouterTree(snapshot, 'params'),
    });
  }

  getRawUrlStructure(routerSnapshot: ActivatedRouteSnapshot): string {
    if (!routerSnapshot.firstChild) {
      return '';
    }
    const path =
      routerSnapshot.firstChild.routeConfig &&
      routerSnapshot.firstChild.routeConfig.path;
    return `${path ? `/${path}` : ''}${this.getRawUrlStructure(
      routerSnapshot.firstChild,
    )}`;
  }

  getPropertiesFromRouterTree(
    routerSnapshot: ActivatedRouteSnapshot,
    keyName: string,
  ): { [key: string]: any } {
    if (!routerSnapshot.firstChild) {
      return {};
    }
    const params = (routerSnapshot.firstChild as any)[keyName] || {};
    return {
      ...params,
      ...this.getPropertiesFromRouterTree(routerSnapshot.firstChild, keyName),
    };
  }
}
